import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { GmailStatusAccount } from '@/hooks/integrations/google';
import {
  getDisconnectErrorMessage,
  isIntegrationConnected,
  isIntegrationLimited,
  isPasswordSetupRequiredError,
  useDisconnectGoogle,
  useGmailAliases,
  useGmailStatus,
  useGoogleOAuthConnect,
  useSetPrimaryAccount,
} from '@/hooks/integrations/google';
import { useDeletionStatus } from '@/hooks/integrations/useDeletionStatus';
import { useDisconnectWithDeletion } from '@/hooks/integrations/useDisconnectWithDeletion';
import { useProfile } from '@/hooks/profile/useProfile';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useSetUserProfileData,
  useUserProfileData,
} from '@/hooks/useUserProfileData';
import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { Toggle } from '@/lib/base/forms/toggle';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating, ConfirmationDialog } from '@/lib/base/overlays';
import { getApiBaseUrl } from '@/lib/config';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { isTransientError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';
import { getUnavailableCapabilities } from '@/lib/integrations/scopeCapabilities';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import UnifiedImportStatus from './imports/UnifiedImportStatus';
import AccountDntEditor from './integrations/AccountDntEditor';
import {
  DeleteSyncedDataChecklist,
  type DeleteSyncedDataChecklistRow,
} from './integrations/DeleteSyncedDataChecklist';
import {
  type IntegrationBenefit,
  IntegrationBenefitsCard,
  IntegrationVisibilityToggle,
} from './integrations/IntegrationCards';
import IntegrationDeletionStatus from './integrations/IntegrationDeletionStatus';
import {
  GoogleBrandMark,
  ProviderLogoTile,
} from './integrations/ProviderBrandMarks';
import { SetPasswordDialog } from './integrations/SetPasswordDialog';
import {
  type DataVisibility,
  VisibilityChoiceModal,
} from './integrations/VisibilityChoiceModal';

const GMAIL_BENEFITS: IntegrationBenefit[] = [
  // Phrased as what the user gets, not which scope Ahoy is granted. "Search and
  // read your email" describes a permission being taken; nobody connects an
  // account to grant a permission, they connect it for the outcome.
  //
  // Keep every line to a similar length: they sit in a two-column grid, so
  // items of wildly different lengths wrap unevenly and leave the columns
  // ragged.
  { label: 'Gmail:', text: 'Replies drafted with the full thread in context' },
  {
    label: 'Calendar:',
    text: 'Times proposed against your real availability',
  },
  { label: 'Meet:', text: 'Video links added to every call Ahoy books' },
  { label: 'Your CRM:', text: 'Email and meetings logged to the right record' },
];

const VISIBILITY_UPDATE_ERROR = "We couldn't update visibility. Try again.";

// Set right before redirecting to Google for the gmail.compose re-consent
// round-trip, consumed once the settings surface remounts on return (see
// ProviderCallback, which honors `oauth_return_url` and lands the user back
// here). Session-scoped (not per-account) since the setting itself is
// per-user, not per-account.
const GMAIL_COMPOSE_RECONCILE_PENDING_KEY = 'gmail_compose_reconcile_pending';
const GMAIL_COMPOSE_DECLINED_ERROR =
  "We couldn't turn on Gmail drafts because compose access wasn't granted.";

interface ReconnectVisibilityBannerProps {
  account: GmailStatusAccount;
  isUpdating: boolean;
  onShareWithTeam: (account: GmailStatusAccount) => void;
  onKeepPrivate: (account: GmailStatusAccount) => void;
}

/**
 * Per-account banner shown when the user reconnects after a disconnect that
 * auto-flipped data visibility to "private". Surfaces the change and gives the
 * user a one-click path to share again, or a way to confirm they want to stay
 * private. Either action clears the underlying flag so the banner does not
 * reappear on subsequent visits.
 *
 * Renders nothing unless the account is currently connected AND at least one
 * provider (gmail or gcal) has the flipped flag set.
 */
function ReconnectVisibilityBanner({
  account,
  isUpdating,
  onShareWithTeam,
  onKeepPrivate,
}: ReconnectVisibilityBannerProps) {
  const handleShare = useCallback(() => {
    onShareWithTeam(account);
  }, [account, onShareWithTeam]);

  const handleKeepPrivate = useCallback(() => {
    onKeepPrivate(account);
  }, [account, onKeepPrivate]);

  const gmailFlipped = !!account.gmail_visibility_flipped_on_disconnect;
  const gcalFlipped = !!account.gcal_visibility_flipped_on_disconnect;

  // Auth-failed accounts use the "Reconnect" button path; suppress the banner
  // until the user has actually completed reconnection.
  const isConnected = account.connection_status === 'connected';

  const label = useMemo(() => {
    if (gmailFlipped && gcalFlipped) {
      return 'Gmail and Calendar';
    }
    if (gmailFlipped) {
      return 'Gmail';
    }
    return 'Calendar';
  }, [gmailFlipped, gcalFlipped]);

  if (!isConnected || (!gmailFlipped && !gcalFlipped)) {
    return null;
  }

  return (
    <div
      className='border-t border-border-secondary p-4'
      data-component='banner'
      data-instance='gmail-reconnect-visibility'
      data-testid='reconnect-visibility-banner'
    >
      <AlertFloating
        color='warning'
        title={`Your ${label} data is set to private`}
        description='We made it private when you disconnected. Want to share it with your team again?'
        confirmLabel={isUpdating ? 'Updating…' : 'Share with team'}
        dismissLabel='Keep private'
        onConfirm={handleShare}
        onClose={handleKeepPrivate}
      />
    </div>
  );
}

interface AdvancedSettingsContentProps {
  orgId: string;
  account: GmailStatusAccount;
  updateAliasFilter: (entityId: string, emailAddress: string) => Promise<void>;
  currentlyUpdatingEntity: string | null;
  canModify: boolean;
  userId: number | undefined;
  /** Kicks off the Google re-consent round-trip requesting the expanded
   * scope set (now including gmail.compose, since the profile flag is on).
   * Wraps the shared `useGoogleOAuthConnect(orgId)` connect fn from the
   * parent so every account's toggle drives the same in-flight state. */
  triggerGoogleReconsent: () => void;
  isReconsenting: boolean;
}

function AdvancedSettingsContent({
  orgId,
  account,
  updateAliasFilter,
  currentlyUpdatingEntity,
  canModify,
  userId,
  triggerGoogleReconsent,
  isReconsenting,
}: AdvancedSettingsContentProps) {
  const { data: aliasesData, isLoading } = useGmailAliases(
    orgId,
    account.social_account_id ?? 0,
    true
  );

  // gmail.compose drafts opt-in — a per-user profile setting, not per-account,
  // but rendered here per the account's Advanced Settings per the design.
  const { formData, setGmailComposeDraftsEnabled, gmailComposeDraftsSaving } =
    useProfile({
      orgId: orgId ? parseInt(orgId, 10) : null,
      userId: userId ?? null,
    });
  const setProfileData = useSetUserProfileData();

  // Post-OAuth reconciliation: runs once, the first time any mounted
  // AdvancedSettingsContent instance sees the pending flag (multiple
  // connected accounts each render one of these, but AccordionPanel keeps
  // every one mounted regardless of expanded state — see accordion.tsx — so
  // this fires on page load after the redirect back from Google, not on
  // accordion expand). The sessionStorage flag is consumed on first read, so
  // a second mounted instance is a no-op.
  const hasReconciled = useRef(false);
  useEffect(() => {
    if (hasReconciled.current) {
      return;
    }
    hasReconciled.current = true;
    if (
      sessionStorage.getItem(GMAIL_COMPOSE_RECONCILE_PENDING_KEY) !== 'true'
    ) {
      return;
    }
    sessionStorage.removeItem(GMAIL_COMPOSE_RECONCILE_PENDING_KEY);

    void fetchWithDefaults('/users/api/gmail-compose/reconcile/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ org_id: orgId }),
    })
      .then(response => response.json())
      .then((data: { granted?: boolean; message?: string }) => {
        if (data.granted) {
          return;
        }
        setProfileData(prev =>
          prev
            ? {
                ...prev,
                user: { ...prev.user, gmail_compose_drafts_enabled: false },
              }
            : prev
        );
        toast.error(data.message ?? GMAIL_COMPOSE_DECLINED_ERROR);
      })
      .catch(err => {
        logger.error(
          'Error reconciling gmail.compose opt-in',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'GmailIntegration' }
        );
      });
  }, [orgId, setProfileData]);

  const handleGmailComposeDraftsToggle = useCallback(
    async (enabled: boolean) => {
      try {
        await setGmailComposeDraftsEnabled(enabled);
      } catch {
        // Toggle already reverted and error surfaced by the hook.
        return;
      }
      if (enabled) {
        sessionStorage.setItem(GMAIL_COMPOSE_RECONCILE_PENDING_KEY, 'true');
        triggerGoogleReconsent();
      }
    },
    [setGmailComposeDraftsEnabled, triggerGoogleReconsent]
  );

  const handleAliasChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      updateAliasFilter(account.gmail_entity_id, value);
    },
    [updateAliasFilter, account.gmail_entity_id]
  );

  const aliasOptions = useMemo(() => {
    const base = [{ label: 'All email addresses', value: '' }];
    if (!aliasesData?.aliases) {
      return base;
    }
    return [
      ...base,
      ...aliasesData.aliases.map(alias => ({
        label: alias.email,
        value: alias.email,
      })),
    ];
  }, [aliasesData?.aliases]);

  return (
    // Padding now provided by the wrapping <AccordionPanel>; only structural
    // spacing remains here.
    <div className='space-y-3'>
      <div>
        {isLoading ? (
          <LoadingIndicator size='sm' />
        ) : (
          <NativeSelect
            label='Filter emails by address'
            hint='Only sync emails sent to this address'
            options={aliasOptions}
            value={account.email_alias_filter || ''}
            onChange={handleAliasChange}
            disabled={currentlyUpdatingEntity === account.gmail_entity_id}
          />
        )}
      </div>
      <div className='pt-3 border-t border-border-secondary'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex-1'>
            <p className='text-sm font-medium text-primary'>
              Put prepared emails into my Gmail drafts
            </p>
            <p className='text-xs text-tertiary mt-0.5'>
              Turning this on asks you to reconnect your Google account to grant
              Gmail draft access.
            </p>
          </div>
          <Toggle
            isSelected={formData.gmail_compose_drafts_enabled}
            onChange={handleGmailComposeDraftsToggle}
            isDisabled={gmailComposeDraftsSaving || isReconsenting}
            size='sm'
          />
        </div>
      </div>
      {userId !== undefined && account.gmail_entity_id ? (
        <div className='pt-3 border-t border-border-secondary'>
          <AccountDntEditor
            orgId={orgId}
            account={account}
            entityId={account.gmail_entity_id}
            userId={userId}
            canModify={canModify}
          />
        </div>
      ) : null}
    </div>
  );
}

interface AccountSetPrimaryButtonProps {
  account: GmailStatusAccount;
  onSetPrimary: (socialAccountId: number) => void;
  isSettingPrimary: boolean;
}

function AccountSetPrimaryButton({
  account,
  onSetPrimary,
  isSettingPrimary,
}: AccountSetPrimaryButtonProps) {
  const handleClick = useCallback(() => {
    if (!account.social_account_id) {
      return;
    }
    onSetPrimary(account.social_account_id);
  }, [onSetPrimary, account.social_account_id]);

  return (
    <Button
      color='secondary'
      size='xs'
      onClick={handleClick}
      disabled={isSettingPrimary}
    >
      {isSettingPrimary ? 'Setting…' : 'Make primary'}
    </Button>
  );
}

interface AccountDisconnectButtonProps {
  account: GmailStatusAccount;
  onDisconnect: (account: GmailStatusAccount) => void;
  isDisconnecting: boolean;
}

function AccountDisconnectButton({
  account,
  onDisconnect,
  isDisconnecting,
}: AccountDisconnectButtonProps) {
  const handleClick = useCallback(
    () => onDisconnect(account),
    [onDisconnect, account]
  );

  return (
    <Button
      color='secondary'
      size='xs'
      onClick={handleClick}
      disabled={isDisconnecting}
    >
      {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
    </Button>
  );
}

interface DisconnectedAccountCardProps {
  account: GmailStatusAccount;
  orgId: string;
  canConnectIntegration: boolean;
  canModifyIntegration: boolean;
  onConnect: () => void;
  connecting: boolean;
  onOpenDeleteSyncedData: (account: GmailStatusAccount) => void;
}

function DisconnectedAccountCard({
  account,
  orgId,
  canConnectIntegration,
  canModifyIntegration,
  onConnect,
  connecting,
  onOpenDeleteSyncedData,
}: DisconnectedAccountCardProps) {
  // Poll both entities independently. A Google account that has both Gmail and
  // Calendar integrations gets one deletion job per entity, so checking only
  // one would miss an active job on the other.
  // useDeletionStatus is a no-op when integrationEntityId is empty.
  const gmailDeletion = useDeletionStatus({
    orgId,
    integrationEntityId: account.gmail_entity_id || '',
  });
  const gcalDeletion = useDeletionStatus({
    orgId,
    integrationEntityId: account.gcal_entity_id || '',
  });

  const hasActiveDeletions =
    gmailDeletion.hasActiveDeletions || gcalDeletion.hasActiveDeletions;

  const handleOpenDelete = useCallback(() => {
    onOpenDeleteSyncedData(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOpenDeleteSyncedData, account.email]);

  const canDelete =
    canModifyIntegration &&
    (!!account.gmail_entity_id || !!account.gcal_entity_id);

  return (
    <div className='bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden'>
      <div className='flex items-center justify-between p-4'>
        <div className='flex items-center gap-3'>
          <img
            src={`${getApiBaseUrl()}/static/images/socialauth/google-logo.svg`}
            alt='Google'
            className='w-8 h-8'
          />
          <div>
            <div className='flex items-center gap-2'>
              <span className='font-medium text-primary'>{account.email}</span>
              <Badge type='color' size='sm' color='gray'>
                Disconnected
              </Badge>
            </div>
            <p className='text-xs text-tertiary'>
              Disconnected · No active sync
            </p>
          </div>
        </div>
        {hasActiveDeletions ? (
          <div className='flex-1 ml-4 space-y-2'>
            {gmailDeletion.hasActiveDeletions && account.gmail_entity_id && (
              <IntegrationDeletionStatus
                orgId={orgId}
                integrationEntityId={account.gmail_entity_id}
              />
            )}
            {gcalDeletion.hasActiveDeletions && account.gcal_entity_id && (
              <IntegrationDeletionStatus
                orgId={orgId}
                integrationEntityId={account.gcal_entity_id}
              />
            )}
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            {canConnectIntegration && (
              <Button
                color='primary'
                size='sm'
                onClick={onConnect}
                disabled={connecting}
              >
                {connecting ? 'Reconnecting…' : 'Reconnect'}
              </Button>
            )}
            {canDelete && (
              <Button color='secondary' size='sm' onClick={handleOpenDelete}>
                Delete synced data
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function buildDeleteChecklistRows(
  account: GmailStatusAccount
): DeleteSyncedDataChecklistRow[] {
  const rows: DeleteSyncedDataChecklistRow[] = [];
  if (account.gmail_entity_id) {
    rows.push({
      entityType: 'ahoy_email_thread',
      label: 'Delete synced emails',
      integrationEntityId: account.gmail_entity_id,
      isChecked: false,
    });
  }
  if (account.gcal_entity_id) {
    rows.push({
      entityType: 'ahoy_meeting',
      label: 'Delete synced calendar events',
      integrationEntityId: account.gcal_entity_id,
      isChecked: false,
    });
  }
  return rows;
}

export default function GmailIntegration() {
  const params = useParams();
  const { orgId } = params; // Get team ID from route params
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canConnectIntegration = hasPermission(
    'ahoy_external_integration',
    'ahoy_object_created'
  );
  const canModifyIntegration = hasPermission(
    'ahoy_external_integration',
    'ahoy_property_updated'
  );

  // Get user ID from profile data
  const { data: profileData } = useUserProfileData();
  const userId = profileData?.user?.id;

  // React Query hooks for data fetching and mutations
  const {
    data: status,
    isLoading: loading,
    error: queryError,
  } = useGmailStatus(orgId);
  const isTransientStatusError =
    queryError != null && isTransientError(queryError);
  const {
    mutateAsync: disconnectAsync,
    isPending: isDisconnecting,
    variables: disconnectVariables,
    error: disconnectError,
  } = useDisconnectGoogle();
  const disconnectFlow = useDisconnectWithDeletion<GmailStatusAccount>({
    orgId,
    buildChecklistRows: buildDeleteChecklistRows,
    disconnect: async account => {
      if (!account.social_account_id) {
        return;
      }
      await disconnectAsync({
        accountId: account.social_account_id,
        orgId: orgId ? parseInt(orgId) : undefined,
      });
    },
    baseDescription:
      'This will disable Gmail, Calendar, and Meet capabilities for this account. You can reconnect any time.',
    toastLabel: 'Account',
  });
  const {
    mutate: setPrimary,
    isPending: isSettingPrimary,
    variables: setPrimaryVariables,
    error: setPrimaryError,
  } = useSetPrimaryAccount();
  const {
    connect,
    isConnecting: connecting,
    error: connectError,
    setError: setConnectError,
  } = useGoogleOAuthConnect(orgId);
  // Wraps `connect` for the gmail.compose re-consent toggle in
  // AdvancedSettingsContent — no visibility choice needed for a scope-only
  // re-consent on an already-connected account, unlike the initial connect
  // flow (which goes through VisibilityChoiceModal).
  const triggerGoogleReconsent = useCallback(() => {
    void connect();
  }, [connect]);

  // Derive connected state from connection_status. Each account's missing
  // connection_status falls back to the top-level `status.connected` boolean
  // (backwards-compat). When there are no accounts, fall back to
  // `status.connected` directly so the legacy boolean is honored.
  const fallbackConnected = status?.connected ?? false;
  const isConnected =
    status?.accounts && status.accounts.length > 0
      ? status.accounts.some(
          account =>
            isIntegrationConnected(
              account.connection_status,
              fallbackConnected
            ) || isIntegrationLimited(account.connection_status)
        )
      : fallbackConnected;
  // Show the accounts panel when at least one account is fully connected OR
  // partially connected (partial_scopes). Auth-failed-only accounts still show
  // the top-level "Connect" button instead.
  const _hasActiveAccounts =
    status?.accounts && status.accounts.length > 0
      ? status.accounts.some(
          account =>
            isIntegrationConnected(
              account.connection_status,
              fallbackConnected
            ) || isIntegrationLimited(account.connection_status)
        )
      : fallbackConnected;
  const unavailableCapabilities = useMemo(() => {
    if (!status?.accounts || status.accounts.length === 0) {
      return new Set<string>();
    }
    // Only consider accounts that can actually serve requests (fully
    // connected or partial_scopes). Auth-failed accounts contribute nothing
    // because their tokens can't be used.
    const functionalAccounts = status.accounts.filter(
      acc =>
        isIntegrationConnected(acc.connection_status, fallbackConnected) ||
        isIntegrationLimited(acc.connection_status)
    );
    if (functionalAccounts.length === 0) {
      return new Set<string>();
    }
    // A capability is globally unavailable only when EVERY functional
    // account is missing at least one required scope for it. If any
    // account has the scope, the capability is available via that account.
    const perAccountUnavailable = functionalAccounts.map(acc =>
      getUnavailableCapabilities(acc.missing_scopes ?? [])
    );
    const [first, ...rest] = perAccountUnavailable;
    const intersection = new Set<string>();
    for (const capability of first) {
      if (rest.every(set => set.has(capability))) {
        intersection.add(capability);
      }
    }
    return intersection;
  }, [status?.accounts, fallbackConnected]);

  // Create a map to track which entity IDs are currently updating
  const [currentlyUpdatingEntity, setCurrentlyUpdatingEntity] = useState<
    string | null
  >(null);

  // State for pending visibility change (for confirmation dialog)
  const [pendingCalendarVisibilityChange, setPendingCalendarVisibilityChange] =
    useState<{
      entityId: string;
      newValue: boolean;
    } | null>(null);
  const [pendingEmailVisibilityChange, setPendingEmailVisibilityChange] =
    useState<{
      entityId: string;
      newValue: boolean;
    } | null>(null);

  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  // Local state for per-item loading tracking
  const disconnectingAccountId = isDisconnecting
    ? (disconnectVariables?.accountId ?? null)
    : null;
  const settingPrimaryAccountId = isSettingPrimary
    ? (setPrimaryVariables?.socialAccountId ?? null)
    : null;

  // Disconnect and delete-synced-data dialogs are managed by the shared
  // disconnectFlow hook above — see useDisconnectWithDeletion.

  // Local state for error dismissal
  const [dismissedError, setDismissedError] = useState(false);

  // Set-password dialog (shown when disconnect 400's with the
  // "set a password first" message — see isPasswordSetupRequiredError).
  const [isSetPasswordDialogOpen, setIsSetPasswordDialogOpen] = useState(false);
  const openSetPasswordDialog = useCallback(
    () => setIsSetPasswordDialogOpen(true),
    []
  );
  const closeSetPasswordDialog = useCallback(
    () => setIsSetPasswordDialogOpen(false),
    []
  );
  const handleSetPasswordSuccess = useCallback(() => {
    // Dismiss the disconnect error so the inline banner clears — the user
    // can now click Disconnect again and proceed.
    setDismissedError(true);
  }, []);

  // Derive error from query, connect, or mutation errors (unless dismissed).
  // For disconnect: surface the backend's actionable copy on 4xx, fall back to
  // the generic friendly copy on 5xx / network / timeout — see
  // getDisconnectErrorMessage.
  const error = dismissedError
    ? null
    : ((isTransientStatusError ? undefined : queryError?.message) ??
      connectError ??
      (disconnectError ? getDisconnectErrorMessage(disconnectError) : null) ??
      setPrimaryError?.message ??
      visibilityError ??
      null);

  // Determine error title based on error source (order matches `error` derivation above)
  const getErrorTitle = () => {
    if (queryError && !isTransientStatusError) {
      return 'Unable to load account status';
    }
    if (connectError) {
      return 'Unable to connect account';
    }
    if (disconnectError) {
      return "We couldn't disconnect this account";
    }
    if (setPrimaryError) {
      return 'Unable to set primary account';
    }
    if (visibilityError) {
      return 'Unable to update visibility setting';
    }
    return 'An error occurred';
  };

  const showSetPasswordAction =
    !(queryError && !isTransientStatusError) &&
    !connectError &&
    isPasswordSetupRequiredError(disconnectError);

  const clearError = useCallback(() => {
    setDismissedError(true);
    setConnectError(null);
  }, [setConnectError]);

  // Reset dismissedError when a new error comes in
  const currentErrorKey =
    (isTransientStatusError ? undefined : queryError?.message) ??
    connectError ??
    disconnectError?.message ??
    setPrimaryError?.message ??
    visibilityError ??
    '';
  const [lastErrorKey, setLastErrorKey] = useState(currentErrorKey);
  if (currentErrorKey !== lastErrorKey) {
    setLastErrorKey(currentErrorKey);
    if (currentErrorKey) {
      setDismissedError(false);
    }
  }

  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);

  const handleConnect = useCallback(() => {
    setIsVisibilityModalOpen(true);
  }, []);

  const handleConfirmVisibility = useCallback(
    (visibility: DataVisibility) => {
      setIsVisibilityModalOpen(false);
      connect(visibility);
    },
    [connect]
  );

  // handleDisconnectClick wraps the shared hook to add the
  // social_account_id guard before opening the dialog.
  const handleDisconnectClick = useCallback(
    (account: GmailStatusAccount) => {
      if (!account.social_account_id) {
        return;
      }
      disconnectFlow.handleDisconnectClick(account);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disconnectFlow.handleDisconnectClick]
  );

  // Delete-synced-data handlers for orphaned accounts come from the shared
  // disconnectFlow hook — no local state needed.

  // Helper to update integration visibility using CRM events API.
  //
  // Always bundles a clear of `ahoy_visibility_flipped_on_disconnect` ("false")
  // alongside the visibility change so that any user-initiated visibility
  // change also dismisses the reconnect banner. The backend property filter
  // drops the flag clear if it's already false, so this is safe as a no-op.
  const updateIntegrationVisibility = useCallback(
    async (
      entityId: string,
      visibility: 'private' | 'org'
    ): Promise<boolean> => {
      if (!orgId || !userId || !entityId) {
        return false;
      }

      setCurrentlyUpdatingEntity(entityId);
      setVisibilityError(null);

      try {
        // Get cached integration object
        const queryKey = [
          'crm',
          parseInt(orgId),
          'objects',
          'ahoy_external_integration',
          'detail',
          entityId,
        ];
        let integrationObject = queryClient.getQueryData<CrmObject>(queryKey);

        // If not in cache, fetch it
        if (!integrationObject) {
          const client = createCRMObjectClient(
            parseInt(orgId),
            'ahoy_external_integration'
          );
          integrationObject = await client.get(entityId);

          if (!integrationObject) {
            throw new Error('Integration not found');
          }

          // Cache it for next time
          queryClient.setQueryData(queryKey, integrationObject);
        }

        // Use EventBuilder to create property update
        const { createEventBuilder } =
          await import('@/lib/crm/core/EventBuilder');
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: integrationObject.last_event_seq,
        });

        builder.propertiesUpdated(entityId, 'ahoy_external_integration', {
          ahoy_default_data_visibility: visibility,
          ahoy_visibility_flipped_on_disconnect: 'false',
        });

        const events = builder.build();
        const client = createCRMObjectClient(
          parseInt(orgId),
          'ahoy_external_integration'
        );
        await client.submitEvents({ events });

        // Optimistically update cache
        queryClient.setQueryData<CrmObject>(queryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            last_event_seq: old.last_event_seq + 1,
            properties: {
              ...old.properties,
              ahoy_default_data_visibility: { value: visibility },
              ahoy_visibility_flipped_on_disconnect: { value: 'false' },
            },
          };
        });

        // Invalidate gmail status query to refresh
        await queryClient.invalidateQueries({
          queryKey: ['google', 'gmail', 'status', orgId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['crm', parseInt(orgId), 'objects'],
        });
        return true;
      } catch (err) {
        console.error(err);
        setVisibilityError(VISIBILITY_UPDATE_ERROR);
        return false;
      } finally {
        setCurrentlyUpdatingEntity(null);
      }
    },
    [orgId, userId, queryClient]
  );

  const handleSetPrimary = useCallback(
    (socialAccountId: number) => {
      if (!orgId) {
        setConnectError('No team selected');
        return;
      }
      setPrimary({ orgId, socialAccountId });
    },
    [orgId, setConnectError, setPrimary]
  );

  const handleCalendarVisibilityToggle = useCallback(
    (entityId: string, currentValue: boolean) => {
      const newValue = !currentValue;
      if (newValue) {
        // Show confirmation when enabling
        setPendingCalendarVisibilityChange({ entityId, newValue });
      } else {
        // Disable without confirmation
        updateIntegrationVisibility(entityId, 'private');
      }
    },
    [setPendingCalendarVisibilityChange, updateIntegrationVisibility]
  );

  const confirmCalendarVisibilityChange = useCallback(() => {
    if (!pendingCalendarVisibilityChange) {
      return;
    }
    updateIntegrationVisibility(
      pendingCalendarVisibilityChange.entityId,
      'org'
    );
    setPendingCalendarVisibilityChange(null);
  }, [
    pendingCalendarVisibilityChange,
    updateIntegrationVisibility,
    setPendingCalendarVisibilityChange,
  ]);

  const cancelCalendarVisibilityChange = useCallback(() => {
    setPendingCalendarVisibilityChange(null);
  }, [setPendingCalendarVisibilityChange]);

  const handleEmailVisibilityToggle = useCallback(
    (entityId: string, currentValue: boolean) => {
      const newValue = !currentValue;
      if (newValue) {
        // Show confirmation when enabling
        setPendingEmailVisibilityChange({ entityId, newValue });
      } else {
        // Disable without confirmation
        updateIntegrationVisibility(entityId, 'private');
      }
    },
    [setPendingEmailVisibilityChange, updateIntegrationVisibility]
  );

  const confirmEmailVisibilityChange = useCallback(() => {
    if (!pendingEmailVisibilityChange) {
      return;
    }
    updateIntegrationVisibility(pendingEmailVisibilityChange.entityId, 'org');
    setPendingEmailVisibilityChange(null);
  }, [
    pendingEmailVisibilityChange,
    updateIntegrationVisibility,
    setPendingEmailVisibilityChange,
  ]);

  const cancelEmailVisibilityChange = useCallback(() => {
    setPendingEmailVisibilityChange(null);
  }, [setPendingEmailVisibilityChange]);

  // Helper to clear the post-disconnect visibility flag without changing
  // visibility itself. Used by the reconnect banner's "Keep private" path —
  // the user has reviewed the setting and is sticking with private.
  const clearVisibilityFlippedFlag = useCallback(
    async (entityId: string): Promise<boolean> => {
      if (!orgId || !userId || !entityId) {
        return false;
      }

      setCurrentlyUpdatingEntity(entityId);
      setVisibilityError(null);

      try {
        const queryKey = [
          'crm',
          parseInt(orgId),
          'objects',
          'ahoy_external_integration',
          'detail',
          entityId,
        ];
        let integrationObject = queryClient.getQueryData<CrmObject>(queryKey);

        if (!integrationObject) {
          const client = createCRMObjectClient(
            parseInt(orgId),
            'ahoy_external_integration'
          );
          integrationObject = await client.get(entityId);
          if (!integrationObject) {
            throw new Error('Integration not found');
          }
          queryClient.setQueryData(queryKey, integrationObject);
        }

        const { createEventBuilder } =
          await import('@/lib/crm/core/EventBuilder');
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: integrationObject.last_event_seq,
        });

        builder.propertyUpdated(
          entityId,
          'ahoy_external_integration',
          'ahoy_visibility_flipped_on_disconnect',
          'false'
        );

        const events = builder.build();
        const client = createCRMObjectClient(
          parseInt(orgId),
          'ahoy_external_integration'
        );
        await client.submitEvents({ events });

        queryClient.setQueryData<CrmObject>(queryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            last_event_seq: old.last_event_seq + 1,
            properties: {
              ...old.properties,
              ahoy_visibility_flipped_on_disconnect: { value: 'false' },
            },
          };
        });

        await queryClient.invalidateQueries({
          queryKey: ['google', 'gmail', 'status', orgId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['crm', parseInt(orgId), 'objects'],
        });
        return true;
      } catch (err) {
        console.error(err);
        setVisibilityError(VISIBILITY_UPDATE_ERROR);
        return false;
      } finally {
        setCurrentlyUpdatingEntity(null);
      }
    },
    [orgId, userId, queryClient]
  );

  // Banner handlers — flip every flipped provider on the account in turn.
  // Sequential and short-circuiting on first failure: each helper resets
  // visibilityError at the start of its call, so if we kept going after a
  // failure the next call would clobber the error message and silently hide
  // the partial failure from the user.
  const handleBannerShareWithTeam = useCallback(
    async (account: GmailStatusAccount) => {
      if (
        account.gmail_visibility_flipped_on_disconnect &&
        account.gmail_entity_id
      ) {
        const ok = await updateIntegrationVisibility(
          account.gmail_entity_id,
          'org'
        );
        if (!ok) {
          return;
        }
      }
      if (
        account.gcal_visibility_flipped_on_disconnect &&
        account.gcal_entity_id
      ) {
        await updateIntegrationVisibility(account.gcal_entity_id, 'org');
      }
    },
    [updateIntegrationVisibility]
  );

  const handleBannerKeepPrivate = useCallback(
    async (account: GmailStatusAccount) => {
      if (
        account.gmail_visibility_flipped_on_disconnect &&
        account.gmail_entity_id
      ) {
        const ok = await clearVisibilityFlippedFlag(account.gmail_entity_id);
        if (!ok) {
          return;
        }
      }
      if (
        account.gcal_visibility_flipped_on_disconnect &&
        account.gcal_entity_id
      ) {
        await clearVisibilityFlippedFlag(account.gcal_entity_id);
      }
    },
    [clearVisibilityFlippedFlag]
  );

  // Helper to update email alias filter using CRM events API
  const updateAliasFilter = useCallback(
    async (entityId: string, emailAddress: string) => {
      if (!orgId || !userId || !entityId) {
        return;
      }

      setCurrentlyUpdatingEntity(entityId);
      setVisibilityError(null);

      try {
        // Get cached integration object
        const queryKey = [
          'crm',
          parseInt(orgId),
          'objects',
          'ahoy_external_integration',
          'detail',
          entityId,
        ];
        let integrationObject = queryClient.getQueryData<CrmObject>(queryKey);

        // If not in cache, fetch it
        if (!integrationObject) {
          const client = createCRMObjectClient(
            parseInt(orgId),
            'ahoy_external_integration'
          );
          integrationObject = await client.get(entityId);

          if (!integrationObject) {
            throw new Error('Integration not found');
          }

          // Cache it for next time
          queryClient.setQueryData(queryKey, integrationObject);
        }

        // Use EventBuilder to create property update
        const { createEventBuilder } =
          await import('@/lib/crm/core/EventBuilder');
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: integrationObject.last_event_seq,
        });

        builder.propertyUpdated(
          entityId,
          'ahoy_external_integration',
          'ahoy_email_alias_filter',
          emailAddress || null // Send null to clear the filter (empty string selects "All email addresses")
        );

        const events = builder.build();
        const client = createCRMObjectClient(
          parseInt(orgId),
          'ahoy_external_integration'
        );
        await client.submitEvents({ events });

        // Optimistically update cache
        const newValue = emailAddress || null;
        queryClient.setQueryData<CrmObject>(queryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            last_event_seq: old.last_event_seq + 1,
            properties: {
              ...old.properties,
              ahoy_email_alias_filter: { value: newValue },
            },
          };
        });

        // Invalidate gmail status query to refresh
        await queryClient.invalidateQueries({
          queryKey: ['google', 'gmail', 'status', orgId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['crm', parseInt(orgId), 'objects'],
        });
      } catch (err) {
        console.error(err);
        setVisibilityError("We couldn't update the alias filter. Try again.");
      } finally {
        setCurrentlyUpdatingEntity(null);
      }
    },
    [orgId, userId, queryClient]
  );

  // checkedDescription comes from the shared disconnectFlow hook.

  // Rendered when there are no last-known accounts to show: a quiet
  // "Reconnecting…" indicator while the status query is transiently failing,
  // or the normal connect pitch otherwise.
  const noAccountsContent = isTransientStatusError ? (
    <p className='text-sm text-tertiary'>Reconnecting…</p>
  ) : (
    <IntegrationBenefitsCard items={GMAIL_BENEFITS} />
  );

  if (loading) {
    return (
      <div className='flex justify-center py-8'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Error message */}
      {error && (
        <AlertFloating
          color='error'
          title={getErrorTitle()}
          description={error}
          onClose={clearError}
          {...(showSetPasswordAction
            ? {
                confirmLabel: 'Set password',
                onConfirm: openSetPasswordDialog,
              }
            : {})}
        />
      )}

      {/* Google Workspace - Primary Platform */}
      <div
        className='space-y-4'
        data-component='settings-section'
        data-instance='google-workspace'
        data-testid='google-workspace-section'
      >
        <div className='flex items-center justify-between gap-4'>
          <div className='flex min-w-0 items-center gap-3'>
            <ProviderLogoTile>
              <GoogleBrandMark />
            </ProviderLogoTile>
            {/* Subtitle names the accounts this covers — without it the two
                provider cards are near-identical and give a user nothing to
                decide between. Mirrors the Microsoft card's subtitle. */}
            <div className='min-w-0'>
              <h3 className='font-semibold text-fg-primary'>
                Google Workspace
              </h3>
              <p className='mt-0.5 text-sm text-fg-tertiary'>
                For Gmail and Google Calendar accounts
              </p>
            </div>
          </div>
          {isConnected ? (
            <span
              data-component='connection-badge'
              data-state='connected'
              data-testid='google-connected-badge'
            >
              <Badge type='color' size='sm' color='success'>
                Connected
              </Badge>
            </span>
          ) : (
            <>
              <span
                data-component='connection-badge'
                data-state='disconnected'
                aria-hidden='true'
                className='hidden'
              />
              {canConnectIntegration && (
                <Button
                  color='primary'
                  size='sm'
                  onClick={handleConnect}
                  disabled={connecting}
                  data-component='action-button'
                  data-instance='connect-google'
                  data-testid='google-connect-button'
                >
                  {connecting ? 'Connecting…' : 'Connect'}
                </Button>
              )}
            </>
          )}
        </div>

        {status &&
        ((status.accounts?.length ?? 0) > 0 ||
          (status.email && status.social_account_id)) ? (
          <>
            {/* Accounts Display — connected, auth_failed, and disconnected */}
            <div className='space-y-3'>
              {(
                status.accounts ||
                (status.email && status.social_account_id
                  ? [
                      {
                        email: status.email,
                        social_account_id: status.social_account_id,
                        social_account_org_id: '',
                        gmail_entity_id: '',
                        gcal_entity_id: '',
                        is_primary: true,
                        needs_reconnect: false,
                        gmail_default_data_visibility: 'org' as const,
                        gcal_default_data_visibility: 'org' as const,
                      },
                    ]
                  : [])
              ).map((account, index) => {
                const isDisconnected =
                  account.connection_status === 'disconnected';

                if (isDisconnected) {
                  return (
                    <DisconnectedAccountCard
                      key={
                        account.gmail_entity_id ||
                        account.gcal_entity_id ||
                        index
                      }
                      account={account}
                      orgId={orgId ?? ''}
                      canConnectIntegration={canConnectIntegration}
                      canModifyIntegration={canModifyIntegration}
                      onConnect={handleConnect}
                      connecting={connecting}
                      onOpenDeleteSyncedData={
                        disconnectFlow.handleOpenDeleteSyncedData
                      }
                    />
                  );
                }

                const isPartialScope =
                  !account.needs_reconnect &&
                  isIntegrationLimited(account.connection_status) &&
                  (account.missing_scopes?.length ?? 0) > 0;

                let subLabel: string;
                if (account.needs_reconnect) {
                  subLabel = 'Authentication expired';
                } else if (isPartialScope) {
                  subLabel = 'Some sync features are paused';
                } else {
                  subLabel = 'Connected account';
                }

                return (
                  <div
                    key={account.social_account_id || index}
                    className='bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden'
                  >
                    <div className='flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between'>
                      <div className='flex min-w-0 items-center gap-3'>
                        <img
                          src={`${getApiBaseUrl()}/static/images/socialauth/google-logo.svg`}
                          alt='Google'
                          className='w-8 h-8 shrink-0'
                        />
                        <div className='min-w-0'>
                          <div className='flex flex-wrap items-center gap-2'>
                            <span className='truncate font-medium text-primary'>
                              {account.email}
                            </span>
                            {account.is_primary && (
                              <Badge type='color' size='sm' color='brand'>
                                Primary
                              </Badge>
                            )}
                            {account.needs_reconnect && (
                              <Badge type='color' size='sm' color='warning'>
                                Expired
                              </Badge>
                            )}
                            {isPartialScope && (
                              <Badge type='color' size='sm' color='warning'>
                                Limited permissions
                              </Badge>
                            )}
                          </div>
                          <p className='text-xs text-tertiary'>{subLabel}</p>
                        </div>
                      </div>
                      <div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
                        {account.needs_reconnect && canConnectIntegration && (
                          <Button
                            color='secondary'
                            size='sm'
                            onClick={handleConnect}
                            disabled={connecting}
                          >
                            {connecting ? 'Reconnecting…' : 'Reconnect'}
                          </Button>
                        )}
                        {isPartialScope && canConnectIntegration && (
                          <Button
                            color='secondary'
                            size='sm'
                            onClick={handleConnect}
                            disabled={connecting}
                          >
                            {connecting ? 'Connecting…' : 'Update permissions'}
                          </Button>
                        )}
                        {!account.needs_reconnect &&
                          !account.is_primary &&
                          account.social_account_id &&
                          canModifyIntegration && (
                            <AccountSetPrimaryButton
                              account={account}
                              onSetPrimary={handleSetPrimary}
                              isSettingPrimary={
                                settingPrimaryAccountId ===
                                account.social_account_id
                              }
                            />
                          )}
                        {account.social_account_id &&
                          canModifyIntegration &&
                          !account.needs_reconnect && (
                            <AccountDisconnectButton
                              account={account}
                              onDisconnect={handleDisconnectClick}
                              isDisconnecting={
                                disconnectingAccountId ===
                                account.social_account_id
                              }
                            />
                          )}
                      </div>
                    </div>
                    {/* Import Status for this account */}
                    {orgId && account.social_account_id && (
                      <div className='border-t border-border-secondary px-4 py-3'>
                        <UnifiedImportStatus
                          orgId={orgId}
                          socialAccountId={account.social_account_id}
                          gcalEnabled={true}
                        />
                      </div>
                    )}
                    {/* Reconnect visibility prompt — only renders when a prior
                      disconnect auto-flipped this account's data to private. */}
                    <ReconnectVisibilityBanner
                      account={account}
                      isUpdating={
                        currentlyUpdatingEntity === account.gmail_entity_id ||
                        currentlyUpdatingEntity === account.gcal_entity_id
                      }
                      onShareWithTeam={handleBannerShareWithTeam}
                      onKeepPrivate={handleBannerKeepPrivate}
                    />
                    {/* Calendar Visibility Toggle */}
                    {orgId && account.gcal_entity_id && (
                      <IntegrationVisibilityToggle
                        title='Make calendar events public to team by default'
                        description='When enabled, calendar events with default visibility will be visible to all team members. This will also apply to previously synced events.'
                        entityId={account.gcal_entity_id}
                        currentVisibility={account.gcal_default_data_visibility}
                        isUpdating={
                          currentlyUpdatingEntity === account.gcal_entity_id
                        }
                        onToggle={handleCalendarVisibilityToggle}
                      />
                    )}
                    {/* Email Visibility Toggle */}
                    {orgId && account.gmail_entity_id && (
                      <IntegrationVisibilityToggle
                        title='Make emails public to team by default'
                        description='When enabled, emails will be visible to all team members. This will also apply to previously synced emails.'
                        entityId={account.gmail_entity_id}
                        currentVisibility={
                          account.gmail_default_data_visibility
                        }
                        isUpdating={
                          currentlyUpdatingEntity === account.gmail_entity_id
                        }
                        onToggle={handleEmailVisibilityToggle}
                      />
                    )}
                    {/* Advanced Settings — uses shared Accordion so the
                      open/close transition matches the rest of the product
                      and the trigger styling stays in sync with the design
                      system. Each account renders an independent accordion. */}
                    {orgId &&
                      account.gmail_entity_id &&
                      account.social_account_id && (
                        <Accordion className='border-t border-[var(--color-border-secondary)]'>
                          <AccordionItem id='advanced-settings'>
                            <AccordionTrigger className='w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'>
                              <span>Advanced settings</span>
                              <AccordionChevron />
                            </AccordionTrigger>
                            <AccordionPanel className='px-4 pb-4 text-sm text-[var(--color-text-secondary)]'>
                              <AdvancedSettingsContent
                                orgId={orgId}
                                account={account}
                                updateAliasFilter={updateAliasFilter}
                                currentlyUpdatingEntity={
                                  currentlyUpdatingEntity
                                }
                                canModify={canModifyIntegration}
                                userId={userId}
                                triggerGoogleReconsent={triggerGoogleReconsent}
                                isReconsenting={connecting}
                              />
                            </AccordionPanel>
                          </AccordionItem>
                        </Accordion>
                      )}
                  </div>
                );
              })}
            </div>

            {/* Add another account button */}
            {canConnectIntegration && (
              <Button
                color='secondary'
                size='sm'
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting ? 'Connecting…' : 'Connect another account'}
              </Button>
            )}

            {/* What this enables */}
            <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
              <h4 className='font-semibold text-primary mb-2 text-sm'>
                Enabled features:
              </h4>
              <ul className='list-disc list-inside text-sm space-y-1 text-tertiary'>
                <li
                  className={
                    unavailableCapabilities.has('Gmail')
                      ? 'line-through opacity-60'
                      : ''
                  }
                >
                  <strong className='text-primary'>Gmail:</strong>{' '}
                  {unavailableCapabilities.has('Gmail')
                    ? "Paused – Gmail permission wasn't granted"
                    : 'Search and read your email'}
                </li>
                <li
                  className={
                    unavailableCapabilities.has('Calendar')
                      ? 'line-through opacity-60'
                      : ''
                  }
                >
                  <strong className='text-primary'>Calendar:</strong>{' '}
                  {unavailableCapabilities.has('Calendar')
                    ? "Paused – Calendar permission wasn't granted"
                    : 'View events and check availability'}
                </li>
                <li
                  className={
                    unavailableCapabilities.has('Meet')
                      ? 'line-through opacity-60'
                      : ''
                  }
                >
                  <strong className='text-primary'>Meet:</strong>{' '}
                  {unavailableCapabilities.has('Meet')
                    ? "Paused – Meet permission wasn't granted"
                    : 'Create and schedule calls'}
                </li>
                <li>Smart scheduling and meeting assistance</li>
                <li>Cross-service integration during conversations</li>
              </ul>
            </div>
          </>
        ) : (
          noAccountsContent
        )}
      </div>

      <ConfirmationDialog
        isOpen={disconnectFlow.isDisconnectDialogOpen}
        variant='warning'
        title='Disconnect Google account'
        description={disconnectFlow.checkedDescription}
        confirmLabel={isDisconnecting ? 'Disconnecting\u2026' : 'Disconnect'}
        showConfirmTextWhileLoading={true}
        keepCancelEnabledWhileLoading={true}
        isLoading={isDisconnecting}
        onConfirm={disconnectFlow.handleConfirmDisconnect}
        onCancel={disconnectFlow.handleCancelDisconnect}
      >
        {canModifyIntegration && (
          <DeleteSyncedDataChecklist
            rows={disconnectFlow.checklistRows}
            onToggle={disconnectFlow.handleChecklistToggle}
            disabled={isDisconnecting}
          />
        )}
      </ConfirmationDialog>

      <SetPasswordDialog
        isOpen={isSetPasswordDialogOpen}
        onClose={closeSetPasswordDialog}
        onSuccess={handleSetPasswordSuccess}
      />

      <ConfirmationDialog
        isOpen={disconnectFlow.disconnectedCardDeleteTarget !== null}
        title={`Delete synced data from ${disconnectFlow.disconnectedCardDeleteTarget?.email ?? ''}`}
        description='Choose what to delete. This permanently removes the selected data from Ahoy. This action cannot be undone.'
        variant='destructive'
        confirmLabel='Delete'
        isLoading={false}
        isConfirmDisabled={
          (disconnectFlow.disconnectedCardDeleteTarget?.rows.filter(
            r => r.isChecked
          ).length ?? 0) === 0
        }
        onConfirm={disconnectFlow.handleConfirmDeleteSyncedData}
        onCancel={disconnectFlow.handleCancelDeleteSyncedData}
        instance='delete-synced-data'
      >
        {canModifyIntegration &&
          disconnectFlow.disconnectedCardDeleteTarget && (
            <DeleteSyncedDataChecklist
              rows={disconnectFlow.disconnectedCardDeleteTarget.rows}
              onToggle={disconnectFlow.handleDeleteRowToggle}
              disabled={false}
            />
          )}
      </ConfirmationDialog>

      {/* Confirmation Dialog for enabling calendar visibility */}
      <ConfirmationDialog
        isOpen={pendingCalendarVisibilityChange !== null}
        variant='warning'
        title='Make calendar events public?'
        description='Enabling this will make your calendar events with default visibility visible to all team members. Previously synced events will also be updated to match this setting.'
        confirmLabel={currentlyUpdatingEntity ? 'Enabling…' : 'Enable'}
        isLoading={!!currentlyUpdatingEntity}
        onConfirm={confirmCalendarVisibilityChange}
        onCancel={cancelCalendarVisibilityChange}
      />

      {/* Confirmation Dialog for enabling email visibility */}
      <ConfirmationDialog
        isOpen={pendingEmailVisibilityChange !== null}
        variant='warning'
        title='Make emails public to team?'
        description='Enabling this will make your emails visible to all team members. Previously synced emails will also be updated to match this setting.'
        confirmLabel={currentlyUpdatingEntity ? 'Enabling…' : 'Enable'}
        isLoading={!!currentlyUpdatingEntity}
        onConfirm={confirmEmailVisibilityChange}
        onCancel={cancelEmailVisibilityChange}
      />

      <VisibilityChoiceModal
        isOpen={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        onConfirm={handleConfirmVisibility}
        integrationLabel='Gmail'
        isConfirming={connecting}
      />
    </div>
  );
}
